import React, { useEffect, useRef, useState } from 'react';

import { ValueBox, ValueStepper } from '@candu-ai/craft/ui';
import { Section } from 'craft/containers/Toolbox/components';
import { getCssResetStyleMessage, useCss } from 'craft/hooks';
import { CssSelectorId } from 'craft/types';

const ARROW_MIN_SIZE = 0;
const ARROW_MAX_SIZE = 100;

export const HotspotArrowSize = ({ nodeIds }: { nodeIds: string[] }) => {
  const { css: arrowCss, setCss: setArrowCss, stylesHierarchy } = useCss(nodeIds, {
    cssSelectorId: CssSelectorId.HotspotArrow,
  });

  const { fallbackValue, fallbackStyle } = stylesHierarchy?.width ?? {
    fallbackValue: undefined,
    fallbackStyle: undefined,
  };

  const width = arrowCss.width ? parseFloat(arrowCss.width) : undefined;
  const defaultValue = fallbackValue ? parseFloat(fallbackValue as string) : undefined;
  const displayValue = width || defaultValue;

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<number | undefined>(displayValue);
  const [inEditMode, setInEditMode] = useState(false);

  const onSetValue = (newValue?: number) => {
    const valueChanged = newValue !== inputValue;
    setInEditMode(true);

    if (valueChanged) {
      setInputValue(newValue);

      // only trigger change when there is no value or the value is within the accepted range
      if (typeof newValue === 'undefined') {
        setArrowCss({ width: undefined, height: undefined });
      } else if (newValue >= ARROW_MIN_SIZE && newValue <= ARROW_MAX_SIZE) {
        setArrowCss({ width: `${newValue}px`, height: `${newValue}px` });
      }
    }
  };

  useEffect(() => {
    // when value or type changes due to external factors (caller auto-adjusted value/type, viewport changed, node changed, etc.)
    // if we're not in edit mode, reflect value/type change in input
    if (!inEditMode) {
      if (displayValue !== inputValue) {
        setInputValue(displayValue);
      }
    }
  }, [displayValue, inEditMode]);

  return (
    <Section name="Arrow">
      <ValueBox label="Size">
        <ValueStepper
          ref={inputRef}
          step={1}
          name="arrow-size"
          placeholder={!inEditMode ? 'unset' : undefined}
          valueSuffix="px"
          editable
          format="inline"
          minValue={ARROW_MIN_SIZE}
          maxValue={ARROW_MAX_SIZE}
          onChange={onSetValue}
          onBlur={() => {
            setInEditMode(false);
          }}
          value={inputValue}
          onResetTooltip={fallbackStyle && getCssResetStyleMessage(fallbackStyle)}
          onReset={
            typeof inputValue !== 'undefined' &&
            typeof fallbackValue !== 'undefined' &&
            inputValue !== defaultValue
              ? () => {
                  setArrowCss({ width: undefined, height: undefined });
                  setInputValue(defaultValue);
                }
              : undefined
          }
        />
      </ValueBox>
    </Section>
  );
};
